import type { DefectStage } from '@felix/domain';
import type { DefectDetail } from './types.js';

/**
 * LGS-17 iş akışı (ADR 0096 §2, §9; `ariza-basamaklar.jpeg`): ana ekranda dört aşama, Workflow
 * sekmesinde on bir basamak ve sorumluları. Hepsi kayıttan türer, saklanmaz. Saf hesap.
 */

export const DEFECT_MAIN_STAGES: readonly DefectStage[] = [
  'reported',
  'under_action',
  'verification',
  'closed',
];

export type StepState = 'done' | 'current' | 'pending' | 'optional';

export interface MainStage {
  stage: DefectStage;
  state: 'done' | 'current' | 'pending';
  /** Aşamanın bilinen anı ya da günü; yoksa `null`. */
  at: string | null;
}

/** Dört ana aşama ve bilinen tarihleri: bildirim, kaptanın işlemi, onarım günü, kapanış. */
export function mainStages(detail: DefectDetail): MainStage[] {
  const index = DEFECT_MAIN_STAGES.indexOf(detail.stage);
  const at: Record<DefectStage, string | null> = {
    reported: detail.reportedAt,
    under_action: index >= 1 ? (detail.captainActionOn ?? null) : null,
    verification: index >= 2 ? (detail.rectifiedOn ?? null) : null,
    closed: detail.closedAt,
  };
  return DEFECT_MAIN_STAGES.map((stage, i) => ({
    stage,
    state: i < index || detail.stage === 'closed' ? 'done' : i === index ? 'current' : 'pending',
    at: at[stage],
  }));
}

/** Basamağın sorumlusu (ortağın tablosu). */
export type StepOwner =
  | 'crew'
  | 'master'
  | 'technical'
  | 'technical_office'
  | 'office'
  | 'master_office'
  | 'authorized';

export interface WorkflowStep {
  key: string;
  stage: DefectStage;
  owner: StepOwner;
  state: StepState;
  /** Bilinen an (kayıttan), kişi adı. */
  at: string | null;
  by: string | null;
}

const blank = (value: string | null | undefined) => !value?.trim();

/**
 * On bir basamak (A1, A10): oluşturma (mürettebat), kaptan değerlendirmesi, iş emri, uygunsuzluk
 * (gerekirse), parça / satın alma (gerekirse), iş emri yürütme ve tamamlama, kaptan onarım
 * doğrulaması, işletme incelemesi (Part C), SMS prosedür takibi, kapanış.
 */
export function workflowSteps(detail: DefectDetail): WorkflowStep[] {
  const links = detail.links;
  const workOrders = (links?.workOrders ?? []).filter((w) => !w.cancelled);
  const allReported = workOrders.length > 0 && workOrders.every((w) => w.reported);
  const anyReported = workOrders.some((w) => w.reported);
  const closed = detail.stage === 'closed';
  const pendingClosure = detail.closure.status === 'pending';
  const shipStep = detail.signatures.steps.find((s) => !s.office);
  const officeStep = detail.signatures.steps.find((s) => s.office);
  const management = detail.officeNotes.filter((n) => n.kind === 'management_action');
  const past = (stage: DefectStage) => DEFECT_ORDER[detail.stage] > DEFECT_ORDER[stage] || closed;
  const captainDone = !blank(detail.immediateAction) || detail.status !== 'open';
  const step = (
    key: string,
    stage: DefectStage,
    owner: StepOwner,
    state: StepState,
    at: string | null = null,
    by: string | null = null,
  ): WorkflowStep => ({ key, stage, owner, state, at, by });
  return [
    step('create', 'reported', 'crew', 'done', detail.reportedAt, detail.reportedBy.name),
    step(
      'captainReview',
      'under_action',
      'master',
      captainDone ? 'done' : 'current',
      detail.captainActionOn,
    ),
    step(
      'workOrder',
      'under_action',
      'master',
      workOrders.length > 0
        ? 'done'
        : past('under_action')
          ? 'optional'
          : captainDone
            ? 'current'
            : 'pending',
    ),
    step(
      'nonConformity',
      'under_action',
      'master',
      (links?.nonConformities.length ?? 0) > 0 ? 'done' : 'optional',
    ),
    step(
      'parts',
      'under_action',
      'technical_office',
      (links?.requisitions.length ?? 0) > 0 || (links?.materials.length ?? 0) > 0
        ? detail.awaitingParts
          ? 'current'
          : 'done'
        : 'optional',
    ),
    step(
      'execution',
      'under_action',
      'technical',
      allReported || past('under_action') ? 'done' : workOrders.length > 0 ? 'current' : 'pending',
    ),
    step(
      'completion',
      'under_action',
      'technical',
      allReported || past('under_action') ? 'done' : anyReported ? 'current' : 'pending',
      detail.rectifiedOn,
    ),
    step(
      'verification',
      'verification',
      'master',
      shipStep?.decision === 'approved' || closed
        ? 'done'
        : detail.stage === 'verification'
          ? 'current'
          : 'pending',
      shipStep?.signature?.at ?? null,
      shipStep?.signature?.name ?? null,
    ),
    step(
      'management',
      'verification',
      'office',
      management.length > 0 || officeStep?.decision === 'approved' || closed
        ? 'done'
        : detail.stage === 'verification'
          ? 'current'
          : 'pending',
      management.at(-1)?.at ?? officeStep?.signature?.at ?? null,
      management.at(-1)?.byName ?? officeStep?.signature?.name ?? null,
    ),
    step(
      'sms',
      'verification',
      'master_office',
      detail.procedureReview !== null
        ? 'done'
        : detail.stage === 'verification'
          ? 'current'
          : 'pending',
    ),
    step(
      'closure',
      'closed',
      'authorized',
      closed ? 'done' : pendingClosure ? 'current' : 'pending',
      detail.closedAt,
      closed ? (officeStep?.signature?.name ?? shipStep?.signature?.name ?? null) : null,
    ),
  ];
}

const DEFECT_ORDER: Readonly<Record<DefectStage, number>> = {
  reported: 0,
  under_action: 1,
  verification: 2,
  closed: 3,
};

/**
 * Kısa yan panel listesi (`ariza-ekran.jpeg` "Basamaklar / Workflow"): dört ana aşama ve
 * sorumlusu; tamamlanan adımda tarih ve kişi.
 */
export function sideSteps(detail: DefectDetail): WorkflowStep[] {
  const steps = workflowSteps(detail);
  const pick = (key: string) => steps.find((s) => s.key === key) as WorkflowStep;
  const create = pick('create');
  const captain = pick('captainReview');
  const verification = pick('verification');
  const closure = pick('closure');
  return [create, captain, verification, closure];
}
